import { useRef, useMemo, useEffect, useCallback, type FC } from "react";
import { Formik, useFormikContext } from "formik";
import { useFormFunnelTracking } from "./hooks/useFormFunnelTracking";
import { FlowForm } from "../flow/steps/components/FlowForm";
import { FormFieldRenderer, QuestionConfig, FieldType } from "./FormFieldRenderer";
import { useFormFunnelValidation } from "./useFormFunnelValidation";
import { EMAIL_MISSPELLED_ERROR } from "../hooks/useEmailTypoWarning";
import { FunnelButton, FunnelSubmitWrapper } from "./FunnelButton";
import { FunnelBackButton } from "./FunnelBackButton";
import { evaluateConditionalVisibility } from "./utils/conditionalVisibility";
import { normalizeFunnelAnswers } from "./utils/normalizeFunnelAnswers";
import { FormFunnelTrustBadge } from "./FormFunnelTrustBadge";
import { useFormFunnelHeadingLevel } from "./FormFunnelHeadingContext";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { SANS_FONT_STACK } from "@portal/app/_components/theme/options/typography";
import isEqual from "lodash/isEqual";
import * as Sentry from "@sentry/nextjs";
import { useEngagementTracking } from "../hooks/useEngagementTracking";

// Single-choice field types that should trigger auto-advance
const SINGLE_CHOICE_TYPES: FieldType[] = ["radio", "pictureChoice"];
const AUTO_ADVANCE_DELAY_MS = 300;

export interface DynamicFormStepProps {
  questions: QuestionConfig[];
  restorationReady?: boolean;
  stepId?: string;
  onComplete: (values: Record<string, unknown>) => void;
  onBack?: () => void;
  savedAnswers?: Record<string, unknown>;
  trustBadgeText?: string;
  headline?: string;
  subtitle?: string;
  /** CMS catalog inspection: include conditional questions regardless of answers. */
  renderAllQuestions?: boolean;
  engagement?: {
    stepId: string;
    stepIndex: number;
    formFunnelId: string;
    formFunnelSessionId: string;
  } | null;
}

const hasAnswer = (answer: unknown) =>
  answer !== undefined &&
  answer !== null &&
  answer !== "" &&
  (!Array.isArray(answer) || answer.length > 0);

function QuestionEngagement({
  engagement,
  fieldKeys,
}: Pick<DynamicFormStepProps, "engagement"> & { fieldKeys: string[] }) {
  useEngagementTracking(
    engagement ? { ...engagement, scope: "step", phase: "questions", fieldKeys } : null
  );
  return null;
}

export const DynamicFormStep: FC<DynamicFormStepProps> = ({
  questions,
  onComplete,
  onBack,
  savedAnswers,
  trustBadgeText,
  headline,
  subtitle,
  renderAllQuestions = false,
  engagement,
  restorationReady = true,
  stepId = questions.map((question) => question.id).join("|"),
}) => {
  const tracking = useFormFunnelTracking();
  const validationSchema = useFormFunnelValidation(questions);
  const submissionStep = useMemo(() => ({ stepId, completed: false }), [stepId]);
  const activeStep = useRef(submissionStep);
  activeStep.current = submissionStep;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const initialValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    questions.forEach((q) => {
      values[q.id] = savedAnswers?.[q.id] ?? (q.type === "multiselect" ? [] : "");
    });
    return values;
  }, [questions, savedAnswers]);

  const getVisibleQuestions = useCallback(
    (values: Record<string, unknown>) =>
      questions.filter((q) => {
        if (renderAllQuestions) return true;
        const dependentValue = q.conditionalLogic?.dependsOn
          ? (values[q.conditionalLogic.dependsOn] ?? savedAnswers?.[q.conditionalLogic.dependsOn])
          : undefined;
        return evaluateConditionalVisibility(q.conditionalLogic, dependentValue);
      }),
    [questions, savedAnswers, renderAllQuestions]
  );

  const handleFormSubmit = useCallback(
    (values: Record<string, unknown>) => {
      if (!mounted.current || activeStep.current !== submissionStep || submissionStep.completed) {
        return;
      }
      submissionStep.completed = true;
      const normalizedValues = normalizeFunnelAnswers(values);
      const normalizedInitialValues = normalizeFunnelAnswers(initialValues);
      getVisibleQuestions(values).forEach((question) => {
        const answer = normalizedValues[question.id];
        const previousAnswer = normalizedInitialValues[question.id];
        const answerPreviouslyProvided = hasAnswer(previousAnswer);
        if (!hasAnswer(answer) && !answerPreviouslyProvided) return;

        tracking.trackEvent("Form Funnel - Question Answered", {
          fieldKey: question.id,
          answer,
          answerPreviouslyProvided,
          answerChanged: answerPreviouslyProvided && !isEqual(answer, previousAnswer),
        });
      });

      onComplete(values);
    },
    [getVisibleQuestions, initialValues, onComplete, submissionStep, tracking]
  );

  return (
    <Formik
      key={stepId}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize
      validateOnMount
    >
      <StepForm
        questions={questions}
        initialValues={initialValues}
        restorationReady={restorationReady}
        getVisibleQuestions={getVisibleQuestions}
        onBack={onBack}
        headline={headline}
        subtitle={subtitle}
        trustBadgeText={trustBadgeText}
        engagement={engagement}
      />
    </Formik>
  );
};

function StepForm({
  initialValues: expectedInitialValues,
  restorationReady,
  getVisibleQuestions,
  onBack,
  headline,
  subtitle,
  trustBadgeText,
  engagement,
}: Omit<DynamicFormStepProps, "onComplete"> & {
  initialValues: Record<string, unknown>;
  getVisibleQuestions: (values: Record<string, unknown>) => QuestionConfig[];
}) {
  const headingLevel = useFormFunnelHeadingLevel();
  const {
    values,
    initialValues,
    handleSubmit,
    submitForm,
    setValues,
    isValid,
    isSubmitting,
    errors,
  } = useFormikContext<Record<string, unknown>>();
  const currentValues = useRef(values);
  currentValues.current = values;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(false);
  const submitting = useRef(false);
  const captured = useRef<{ name: string; value: string }[] | null>(null);
  const handledSelections = useRef(new Set<string>());
  const captureSelections = useCallback((form: HTMLFormElement | null) => {
    if (!form || captured.current !== null) return;
    // Hydration preserves native checked state, but the next controlled render can clear it.
    captured.current = Array.from(
      form.querySelectorAll<HTMLInputElement>('input[type="radio"]:checked:not(:disabled)')
    ).map(({ name, value }) => ({ name, value }));
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const handleFieldChange = useCallback(
    (selectionValues = currentValues.current) => {
      if (timer.current) clearTimeout(timer.current);
      if (!mounted.current || submitting.current) return;
      const visible = getVisibleQuestions(selectionValues);
      if (visible.length !== 1 || !SINGLE_CHOICE_TYPES.includes(visible[0].type)) return;
      timer.current = setTimeout(() => {
        timer.current = null;
        const currentVisible = getVisibleQuestions(currentValues.current);
        if (currentVisible.length !== 1 || !SINGLE_CHOICE_TYPES.includes(currentVisible[0].type))
          return;
        submitting.current = true;
        void submitForm()
          .catch((error) => Sentry.captureException(error))
          .finally(() => {
            submitting.current = false;
          });
      }, AUTO_ADVANCE_DELAY_MS);
    },
    [getVisibleQuestions, submitForm]
  );

  useEffect(() => {
    if (!restorationReady || !isEqual(initialValues, expectedInitialValues)) return;
    const selections = captured.current;
    if (!selections?.length) return;
    captured.current = [];
    const recovered: Record<string, unknown> = {};
    for (const selection of selections) {
      const question = getVisibleQuestions(values).find((q) => q.id === selection.name);
      if (
        !question ||
        question.disabled ||
        !SINGLE_CHOICE_TYPES.includes(question.type) ||
        handledSelections.current.has(question.id) ||
        hasAnswer(values[question.id]) ||
        hasAnswer(expectedInitialValues[question.id])
      )
        continue;
      const option = question.options?.find((option) => String(option.value) === selection.value);
      if (option) recovered[question.id] = option.value;
    }
    if (!Object.keys(recovered).length) return;
    const nextValues = { ...values, ...recovered };
    void setValues(nextValues, true).catch((error) => Sentry.captureException(error));
    handleFieldChange(nextValues);
  }, [
    restorationReady,
    initialValues,
    expectedInitialValues,
    getVisibleQuestions,
    values,
    setValues,
    handleFieldChange,
  ]);

  const visibleQuestions = getVisibleQuestions(values);
  const onlyTypoWarning =
    Object.keys(errors).length === 1 &&
    Object.values(errors).every((e) => e === EMAIL_MISSPELLED_ERROR);
  const isAutoAdvancingStep =
    visibleQuestions.length === 1 &&
    visibleQuestions[0].required === true &&
    SINGLE_CHOICE_TYPES.includes(visibleQuestions[0].type);

  return (
    <FlowForm
      ref={captureSelections}
      onChangeCapture={(event) => {
        if (event.target instanceof HTMLInputElement && event.target.type === "radio") {
          handledSelections.current.add(event.target.name);
        }
      }}
      onSubmit={handleSubmit}
      data-testid="funnel-step"
    >
      <QuestionEngagement
        engagement={engagement}
        fieldKeys={visibleQuestions.map((question) => question.id)}
      />
      {onBack && (
        <FunnelBackButton
          appearance="text"
          onClick={onBack}
          disabled={isSubmitting}
          data-testid="funnel-back-button"
          sx={{ alignSelf: "flex-start", mb: -1 }}
        >
          Back
        </FunnelBackButton>
      )}
      {headline && (
        <FunnelHeadlineGroup>
          <FunnelHeadline as={headingLevel} data-testid="funnel-headline">
            {headline}
          </FunnelHeadline>
          {subtitle && <FunnelSubtitle data-testid="funnel-subtitle">{subtitle}</FunnelSubtitle>}
        </FunnelHeadlineGroup>
      )}
      {visibleQuestions.map((q, questionIndex) => (
        <FormFieldRenderer
          key={q.id}
          question={{ ...q, disabled: q.disabled || isSubmitting }}
          isPrimaryHeading={!headline && questionIndex === 0}
          onChange={() => handleFieldChange()}
          onReselect={isAutoAdvancingStep ? () => handleFieldChange() : undefined}
        />
      ))}

      {!isAutoAdvancingStep && (
        <FunnelSubmitWrapper width="100%" sx={{ mt: 1, pt: 0 }}>
          <FunnelButton
            type="submit"
            data-navigation-action="continue"
            disabled={(!isValid && !onlyTypoWarning) || isSubmitting}
            data-testid="funnel-continue-button"
            sx={{ maxWidth: "none", minHeight: 56, fontSize: "1.1rem" }}
          >
            Continue
          </FunnelButton>
        </FunnelSubmitWrapper>
      )}

      {trustBadgeText && <FormFunnelTrustBadge text={trustBadgeText} />}
    </FlowForm>
  );
}

const FunnelHeadlineGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 8px 0 10px;
  text-align: center;
`;

const FunnelHeadline = styled("h1")`
  margin: 0;
  font-family: ${SANS_FONT_STACK};
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.palette.primary.main};
`;

const FunnelSubtitle = styled("p")`
  margin: 0;
  font-family: ${SANS_FONT_STACK};
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.dream.grayMediumDark};
`;
